import { useMemo, useState } from 'react';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Inbox,
  List,
  Megaphone,
  Plus,
  Share2,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { PostDialog } from '@/components/social/PostDialog';
import { useProfiles } from '@/features/users/hooks';
import {
  useRescheduleSocialPost,
  useSocialPosts,
  type SocialPostWithDetails,
} from '@/features/social/hooks';
import {
  PLATFORM_ICONS,
  PLATFORM_LABELS,
  RESCHEDULABLE_STATUSES,
  SOCIAL_PLATFORMS,
  SOCIAL_STATUSES,
  STATUS_DOT_CLASS,
  STATUS_LABELS,
  STATUS_PILL_CLASS,
  TYPE_CHIP_CLASS,
} from '@/features/social/constants';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import type { SocialPlatform, SocialPostStatus, SocialPostType } from '@/types/database';

// ---------------------------------------------------------------------------
// Local-date helpers — same approach as the team CalendarPage: everything
// keys on LOCAL `YYYY-MM-DD` strings (scheduled_date is a date column).
// ---------------------------------------------------------------------------

function toKey(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function formatTimeLabel(time: string): string {
  const [hStr, mStr] = time.split(':');
  let h = Number(hStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return mStr === '00' ? `${h} ${suffix}` : `${h}:${mStr} ${suffix}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_LABEL = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});

const ALL = '__all__';
const UNASSIGNED = '__unassigned__';
const DRAG_MIME = 'application/x-atlas-social-post';

type ViewMode = 'calendar' | 'list';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SocialMediaPage() {
  const [view, setView] = useState<ViewMode>('calendar');
  const [cursor, setCursor] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  // Filters (single-choice with an "All" option each — SOCIAL.md §2.1).
  const [platformFilter, setPlatformFilter] = useState<string>(ALL);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [typeFilter, setTypeFilter] = useState<string>(ALL);
  const [assigneeFilter, setAssigneeFilter] = useState<string>(ALL);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<SocialPostWithDetails | null>(null);
  const [createDate, setCreateDate] = useState<string | undefined>(undefined);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  const { data: posts, isLoading } = useSocialPosts();
  const { data: profiles } = useProfiles();
  const reschedule = useRescheduleSocialPost();

  const members = useMemo(
    () =>
      (profiles ?? []).filter((p) => p.is_active && p.role !== 'external_lender'),
    [profiles],
  );

  const filteredPosts = useMemo(() => {
    return (posts ?? []).filter((post) => {
      if (typeFilter !== ALL && post.post_type !== (typeFilter as SocialPostType)) {
        return false;
      }
      if (statusFilter !== ALL && post.status !== (statusFilter as SocialPostStatus)) {
        return false;
      }
      if (
        platformFilter !== ALL &&
        !post.targets.some((t) => t.platform === (platformFilter as SocialPlatform))
      ) {
        return false;
      }
      if (assigneeFilter === UNASSIGNED && post.assignee_id !== null) return false;
      if (
        assigneeFilter !== ALL &&
        assigneeFilter !== UNASSIGNED &&
        post.assignee_id !== assigneeFilter
      ) {
        return false;
      }
      return true;
    });
  }, [posts, typeFilter, statusFilter, platformFilter, assigneeFilter]);

  const postsByDay = useMemo(() => {
    const map = new Map<string, SocialPostWithDetails[]>();
    for (const post of filteredPosts) {
      if (!post.scheduled_date) continue;
      const list = map.get(post.scheduled_date);
      if (list) list.push(post);
      else map.set(post.scheduled_date, [post]);
    }
    return map;
  }, [filteredPosts]);

  const unscheduled = useMemo(
    () => filteredPosts.filter((p) => !p.scheduled_date),
    [filteredPosts],
  );

  // The visible grid spans from the Sunday on/before the 1st to the
  // Saturday on/after the last day of the month.
  const weeks = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const start = addDays(first, -first.getDay());
    const end = addDays(last, 6 - last.getDay());
    const days: Date[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      days.push(new Date(d));
    }
    const wk: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) wk.push(days.slice(i, i + 7));
    return wk;
  }, [cursor]);

  const todayStr = toKey(new Date());
  const postById = useMemo(() => {
    const map = new Map<string, SocialPostWithDetails>();
    for (const p of posts ?? []) map.set(p.id, p);
    return map;
  }, [posts]);

  const openCreate = (date?: string) => {
    setEditingPost(null);
    setCreateDate(date);
    setDialogOpen(true);
  };

  const openEdit = (post: SocialPostWithDetails) => {
    setEditingPost(post);
    setCreateDate(undefined);
    setDialogOpen(true);
  };

  const handleDrop = (postId: string, date: string | null) => {
    const post = postById.get(postId);
    if (!post) return;
    if (post.scheduled_date === date) return;
    reschedule.mutate(
      { id: postId, scheduled_date: date },
      {
        onError: (err) =>
          toast.error(
            err instanceof Error ? err.message : 'Could not reschedule the post.',
          ),
      },
    );
  };

  const dropHandlers = (date: string | null) => ({
    onDragOver: (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes(DRAG_MIME)) {
        e.preventDefault();
        setDragOverKey(date ?? 'tray');
      }
    },
    onDragLeave: () => setDragOverKey(null),
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDragOverKey(null);
      const id = e.dataTransfer.getData(DRAG_MIME);
      if (id) handleDrop(id, date);
    },
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Social media"
        actions={
          <Button onClick={() => openCreate()}>
            <Plus className="h-4 w-4" />
            New post
          </Button>
        }
      />

      {/* ───────────── Toolbar ───────────── */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
        {view === 'calendar' && (
          <>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Previous month"
                onClick={() =>
                  setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))
                }
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const now = new Date();
                  setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
                }}
              >
                Today
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Next month"
                onClick={() =>
                  setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))
                }
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <span className="min-w-36 text-sm font-medium text-foreground">
              {MONTH_LABEL.format(cursor)}
            </span>
          </>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All platforms</SelectItem>
              {SOCIAL_PLATFORMS.map((p) => (
                <SelectItem key={p} value={p}>
                  {PLATFORM_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All statuses</SelectItem>
              {SOCIAL_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-8 w-28 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All types</SelectItem>
              <SelectItem value="organic">Organic</SelectItem>
              <SelectItem value="ad">Ads</SelectItem>
            </SelectContent>
          </Select>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="h-8 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All assignees</SelectItem>
              <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
              {members.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.full_name || m.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* View toggle */}
          <div className="flex items-center rounded-lg border border-input bg-card p-0.5">
            <button
              type="button"
              onClick={() => setView('calendar')}
              aria-label="Calendar view"
              className={cn(
                'flex h-7 items-center gap-1.5 rounded-md px-2 text-xs transition-colors',
                view === 'calendar'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-label="List view"
              className={cn(
                'flex h-7 items-center gap-1.5 rounded-md px-2 text-xs transition-colors',
                view === 'list'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <List className="h-3.5 w-3.5" />
              List
            </button>
          </div>
        </div>
      </div>

      {/* ───────────── Body ───────────── */}
      {view === 'calendar' ? (
        <div className="flex gap-4">
          {/* Month grid */}
          <div className="min-w-0 flex-1 overflow-x-auto">
            <div className="min-w-[760px] overflow-hidden rounded-xl border border-border bg-card">
              <div className="grid grid-cols-7 border-b border-border bg-secondary/40">
                {WEEKDAYS.map((d) => (
                  <div
                    key={d}
                    className="px-2 py-2 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {isLoading ? (
                <div className="grid grid-cols-7 gap-px bg-border">
                  {Array.from({ length: 35 }).map((_, i) => (
                    <div key={i} className="min-h-28 bg-card p-2">
                      <Skeleton className="mb-2 h-4 w-6" />
                      <Skeleton className="h-5 w-full" />
                    </div>
                  ))}
                </div>
              ) : (
                weeks.map((week, wi) => (
                  <div
                    key={wi}
                    className={cn(
                      'grid grid-cols-7 gap-px bg-border',
                      wi > 0 && 'border-t border-border',
                    )}
                  >
                    {week.map((day) => {
                      const key = toKey(day);
                      const inMonth = day.getMonth() === cursor.getMonth();
                      const isToday = key === todayStr;
                      const dayPosts = postsByDay.get(key) ?? [];
                      return (
                        <div
                          key={key}
                          role="button"
                          tabIndex={0}
                          aria-label={`Add post on ${key}`}
                          onClick={() => openCreate(key)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') openCreate(key);
                          }}
                          {...dropHandlers(key)}
                          className={cn(
                            'group min-h-28 cursor-pointer space-y-1 p-1.5 transition-colors',
                            inMonth
                              ? 'bg-card hover:bg-secondary/30'
                              : 'bg-secondary/20 hover:bg-secondary/40',
                            dragOverKey === key && 'bg-primary/10 ring-1 ring-inset ring-primary/50',
                          )}
                        >
                          <div className="flex items-center justify-between px-0.5">
                            <span
                              className={cn(
                                'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                                isToday
                                  ? 'bg-primary font-semibold text-primary-foreground'
                                  : inMonth
                                    ? 'text-foreground'
                                    : 'text-muted-foreground/60',
                              )}
                            >
                              {day.getDate()}
                            </span>
                            <Plus className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-60" />
                          </div>

                          {dayPosts.map((post) => (
                            <PostCard key={post.id} post={post} onOpen={openEdit} />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Unscheduled tray — drop a card here to unschedule it. */}
          <div
            {...dropHandlers(null)}
            className={cn(
              'hidden w-56 shrink-0 flex-col gap-2 rounded-xl border border-dashed border-border bg-card p-3 lg:flex',
              dragOverKey === 'tray' && 'border-primary/60 bg-primary/5',
            )}
          >
            <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <Inbox className="h-3.5 w-3.5" />
              Unscheduled
            </p>
            {unscheduled.length === 0 ? (
              <p className="text-xs text-muted-foreground">No unscheduled posts.</p>
            ) : (
              unscheduled.map((post) => (
                <PostCard key={post.id} post={post} onOpen={openEdit} />
              ))
            )}
          </div>
        </div>
      ) : (
        <ListView posts={filteredPosts} isLoading={isLoading} onOpen={openEdit} />
      )}

      {!isLoading && (posts ?? []).length === 0 && view === 'calendar' && (
        <p className="flex items-center justify-center gap-2 pb-4 text-sm text-muted-foreground">
          <Share2 className="h-4 w-4" />
          No posts yet.
        </p>
      )}

      <PostDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditingPost(null);
        }}
        post={editingPost}
        defaultDate={createDate}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar / tray card
// ---------------------------------------------------------------------------

function PostCard({
  post,
  onOpen,
}: {
  post: SocialPostWithDetails;
  onOpen: (post: SocialPostWithDetails) => void;
}) {
  const draggable = RESCHEDULABLE_STATUSES.has(post.status);
  const platforms = [...new Set(post.targets.map((t) => t.platform))];
  return (
    <button
      type="button"
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_MIME, post.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={(e) => {
        e.stopPropagation();
        onOpen(post);
      }}
      className={cn(
        'block w-full rounded-md border-l-2 px-1.5 py-1 text-left text-xs transition-colors',
        TYPE_CHIP_CLASS[post.post_type],
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
      )}
      title={[
        post.title,
        STATUS_LABELS[post.status],
        post.post_type === 'ad'
          ? `Ad${post.campaign ? ` · ${post.campaign.name}` : ''}`
          : null,
        post.assignee?.full_name && `→ ${post.assignee.full_name}`,
      ]
        .filter(Boolean)
        .join(' · ')}
    >
      <span className="flex items-center gap-1">
        <span
          className={cn('h-1.5 w-1.5 shrink-0 rounded-full', STATUS_DOT_CLASS[post.status])}
        />
        {post.scheduled_time && (
          <span className="shrink-0 font-medium opacity-75">
            {formatTimeLabel(post.scheduled_time.slice(0, 5))}
          </span>
        )}
        <span className="truncate">{post.title}</span>
      </span>
      <span className="mt-0.5 flex items-center gap-1 opacity-70">
        {platforms.slice(0, 4).map((p) => {
          const Icon = PLATFORM_ICONS[p];
          return <Icon key={p} className="h-3 w-3" />;
        })}
        {platforms.length > 4 && (
          <span className="text-[10px]">+{platforms.length - 4}</span>
        )}
        {post.post_type === 'ad' && (
          <span className="ml-auto flex items-center gap-0.5 rounded bg-violet-600/90 px-1 text-[9px] font-semibold text-white">
            <Megaphone className="h-2.5 w-2.5" />
            AD
          </span>
        )}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------

function ListView({
  posts,
  isLoading,
  onOpen,
}: {
  posts: SocialPostWithDetails[];
  isLoading: boolean;
  onOpen: (post: SocialPostWithDetails) => void;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <EmptyState
        icon={Share2}
        title="No posts match"
      />
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Post</TableHead>
            <TableHead>Platforms</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Assignee</TableHead>
            <TableHead>Scheduled</TableHead>
            <TableHead>Published</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {posts.map((post) => {
            const platforms = [...new Set(post.targets.map((t) => t.platform))];
            const firstLive = post.targets.find((t) => t.external_url);
            const publishedTarget = post.targets.find((t) => t.published_at);
            return (
              <TableRow
                key={post.id}
                className="cursor-pointer"
                onClick={() => onOpen(post)}
              >
                <TableCell className="max-w-72">
                  <p className="truncate font-medium">{post.title}</p>
                  {post.master_copy && (
                    <p className="truncate text-xs text-muted-foreground">
                      {post.master_copy.split('\n')[0]}
                    </p>
                  )}
                </TableCell>
                <TableCell>
                  <span className="flex items-center gap-1 text-muted-foreground">
                    {platforms.length === 0 ? (
                      <span className="text-xs">—</span>
                    ) : (
                      platforms.map((p) => {
                        const Icon = PLATFORM_ICONS[p];
                        return (
                          <Icon key={p} className="h-3.5 w-3.5" aria-label={PLATFORM_LABELS[p]} />
                        );
                      })
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  {post.post_type === 'ad' ? (
                    <span className="flex items-center gap-1 text-xs font-medium text-violet-700">
                      <Megaphone className="h-3 w-3" />
                      Ad
                      {post.campaign && (
                        <span className="truncate font-normal text-muted-foreground">
                          · {post.campaign.name}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Organic</span>
                  )}
                </TableCell>
                <TableCell>
                  <span
                    className={cn(
                      'inline-flex rounded-full border px-2 py-0.5 text-[10px] font-medium',
                      STATUS_PILL_CLASS[post.status],
                    )}
                  >
                    {STATUS_LABELS[post.status]}
                  </span>
                </TableCell>
                <TableCell>
                  {post.assignee ? (
                    <span className="flex items-center gap-1.5">
                      <Avatar className="h-6 w-6">
                        {post.assignee.avatar_url && (
                          <AvatarImage src={post.assignee.avatar_url} />
                        )}
                        <AvatarFallback className="text-[9px]">
                          {initials(post.assignee.full_name || post.assignee.email || '?')}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden truncate text-xs xl:inline">
                        {post.assignee.full_name || post.assignee.email}
                      </span>
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">
                  {post.scheduled_date ? (
                    <>
                      {post.scheduled_date}
                      {post.scheduled_time && (
                        <span className="text-muted-foreground">
                          {' '}
                          {formatTimeLabel(post.scheduled_time.slice(0, 5))}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted-foreground">Unscheduled</span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs">
                  {firstLive ? (
                    <a
                      href={firstLive.external_url!}
                      target="_blank"
                      rel="noreferrer noopener"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1 text-primary hover:underline"
                    >
                      {publishedTarget?.published_at
                        ? new Date(publishedTarget.published_at).toLocaleDateString()
                        : 'Live'}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : publishedTarget?.published_at ? (
                    new Date(publishedTarget.published_at).toLocaleDateString()
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
